import { Button } from 'react-bootstrap';
import React, { useMemo, useState } from 'react';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';

import { formatMoney } from '../../util/utils';
import useApi from '../../api/backendApiContext';
import { Invoice, INVOICE_TYPE_ISSUED, IssuedInvoice } from '../types';
import { isInvoicePayable } from '../utils';
import { InvoiceTable } from './InvoiceTable';
import { ConfirmModal } from '../../commons/components/ConfirmModal';
import { v4 as uuid } from 'uuid';
import { openUrlInNewTab } from '../../util/window-utils';
import clsx from 'clsx';
import { usePayAllFeatureSwitcher } from '../hooks/usePayAllFeatureSwitcher';
import { PayNowButton } from './PayNowButton';

export const PayAllButton = (props: {
  invoices: Invoice[];
  smallSize?: boolean;
  source: string;
  dataTest: string;
  hideIfOneInvoice?: boolean;
}) => {
  const { invoices, smallSize, source, dataTest, hideIfOneInvoice } = props;
  const analytics = useAnalytics();
  const { billingApi } = useApi();

  const payableInvoices = useMemo(
    () =>
      invoices.filter(
        (x) => x.type === INVOICE_TYPE_ISSUED && isInvoicePayable(x)
      ) as IssuedInvoice[],
    [invoices]
  );
  const [invoicesModalKey, setInvoicesModalKey] = useState<string | null>(null);
  const [dialogInvoiceIds, setDialogInvoiceIds] = useState<string[]>([]);
  const dialogInvoices = useMemo(() => {
    if (invoicesModalKey == null || !invoices) return [];
    const issuedInvoices = invoices.filter(
      (x) => x.type === INVOICE_TYPE_ISSUED
    ) as IssuedInvoice[];

    return dialogInvoiceIds.flatMap((id) => issuedInvoices.find((x) => x.id === id) || []);
  }, [dialogInvoiceIds, invoices, invoicesModalKey]);

  const totalAmountDue = payableInvoices.reduce((acc, curr) => acc + curr.amountDue, 0);
  const { isEnabled: isPayAllFeatureEnabled } = usePayAllFeatureSwitcher();

  return (
    <>
      {payableInvoices.length === 1
        ? !hideIfOneInvoice && (
            <PayNowButton invoice={payableInvoices[0]} source={source} dataTest={dataTest} />
          )
        : payableInvoices.length > 0 && (
            <Button
              variant="primary"
              className={clsx('billing__pay-all-button', {
                'growegy-button--small': smallSize,
                'growegy-font12': smallSize,
                'w-100': !smallSize,
              })}
              data-test={dataTest}
              onClick={async (e) => {
                e?.stopPropagation();
                if (isPayAllFeatureEnabled) {
                  try {
                    const url =
                      payableInvoices.length === 1
                        ? payableInvoices[0].hostedInvoiceUrl
                        : (await billingApi.getPayAllLink()).url;
                    openUrlInNewTab(url, {
                      analytics,
                      analyticsEvent: AnalyticsEvent.BILLING_PAY_ALL_OPEN,
                      properties: { source },
                    });
                  } catch {
                    setDialogInvoiceIds(payableInvoices.map((x) => x.id));
                    setInvoicesModalKey(uuid());
                  }
                } else {
                  if (payableInvoices.length === 1) {
                    openUrlInNewTab(payableInvoices[0].hostedInvoiceUrl, {
                      analytics,
                      analyticsEvent: AnalyticsEvent.BILLING_INVOICE_OPEN,
                      properties: { source },
                    });
                  } else {
                    setDialogInvoiceIds(payableInvoices.map((x) => x.id));
                    setInvoicesModalKey(uuid());
                  }
                }
              }}
            >
              {`Pay ${formatMoney(`${totalAmountDue}`)}`}
            </Button>
          )}
      {invoicesModalKey && (
        <ConfirmModal
          key={invoicesModalKey}
          title="Pay your invoices as soon as possible"
          message="All past due invoices have the potential to negatively impact your credit score and we are unable to alter the reports."
          className="billing__invoices-modal"
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={true}
          ariaHideApp={false}
          footer={{
            type: 'hide',
            cancel: () => {},
          }}
          onClose={() => setDialogInvoiceIds([])}
        >
          <InvoiceTable
            invoices={dialogInvoices}
            header={'Invoices'}
            context={source}
            reverseOrder={true}
            hidePayAll={true}
          />
        </ConfirmModal>
      )}
    </>
  );
};
